import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Container,
  Backdrop,
  CircularProgress,
  Avatar,
  Fade,
  Grow,
  useTheme,
  useMediaQuery,
  Alert
} from '@mui/material';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { keyframes } from '@mui/system';
import api from '../utils/api';
import { useTranslation } from '../admin/hooks/useTranslation';

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const VerifyEmailPage = () => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleVerify = async () => {
    setLoading(true);
    try {
      await api.get(`/auth/verify/mail/${code}`);
      await refresh?.();
      setMessage(t('verifyEmail.successMessage', 'Email successfully verified.'));
      setVerified(true);
      setTimeout(() => navigate('/profile'), 2000);
    } catch (err: any) {
      setMessage(err.response?.data?.message || t('verifyEmail.failureMessage', 'Email verification failed.'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    setLoading(true);
    try {
      await api.get('/auth/verify/mail/send');
      setMessage(t('verifyEmail.codeSentMessage', 'Verification code sent. Check your email.'));
    } catch (err: any) {
      setMessage(err.response?.data?.message || t('verifyEmail.sendCodeFailed', 'Failed to send verification code.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
        {verified ? (
          <Grow in={true}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon 
                color="success" 
                sx={{ fontSize: 80, mb: 2, animation: `${pulse} 2s infinite` }} 
              />
              <Typography variant="h5" gutterBottom>
                {t('verifyEmail.emailVerifiedTitle', 'Email Verified!')}
              </Typography>
              <Typography sx={{ mb: 3 }}>
                {t('verifyEmail.redirectMessage', 'Your email has been successfully verified. Redirecting to your profile...')}
              </Typography>
              <CircularProgress />
            </Box>
          </Grow>
        ) : (
          <Fade in={true}>
            <Box maxWidth={400} mx="auto">
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Avatar
                  src="/logo192.png"
                  sx={{ 
                    width: 60, 
                    height: 60, 
                    mx: 'auto',
                    mb: 2
                  }}
                />
                <Typography variant="h5" gutterBottom>
                  {t('verifyEmail.verifyEmailTitle', 'Verify Your Email')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('verifyEmail.enterCodeMessage', 'Please enter the verification code sent to your email')}
                </Typography>
              </Box>
              
              <TextField
                fullWidth
                margin="normal"
                label={t('verifyEmail.verificationCodeLabel', 'Verification Code')}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              
              <Box display="flex" gap={2} mt={2}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleVerify}
                  disabled={loading}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : t('verifyEmail.verifyButton', 'Verify')}
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleSendCode}
                  disabled={loading}
                  fullWidth
                >
                  {t('verifyEmail.resendButton', 'Resend Code')}
                </Button>
              </Box>
              
              {message && (
                <Alert 
                  severity={message.startsWith('✅') ? 'success' : 'error'}
                  sx={{ mt: 2 }}
                >
                  {message}
                </Alert>
              )}
            </Box>
          </Fade>
        )}
      </Paper>

      <Backdrop
        open={loading}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default VerifyEmailPage;